import { gnomeSpinePackAssets } from "./gnomeSpinePackAssets.generated";
import {
    resolveGnomePackIds,
    type GnomeSpinePackAsset,
} from "./gnomeSpinePackManifest.ts";
import {
    createGnomeSpineAssetTransport,
    type GnomeAssetPriority,
    type GnomeAssetLease,
} from "./gnomeSpineAssetTransport.ts";
import type { GnomeEquipmentSelection } from "./gnomeSpineEquipment.ts";

// No Pixi/Spine imports: the app shell can fetch bytes without evaluating a renderer.
export const gnomeAssetTransport = createGnomeSpineAssetTransport();
export const gnomeAssetFingerprint = gnomeSpinePackAssets.fingerprint;

export function gnomePackFileUrls(pack: GnomeSpinePackAsset): string[] {
    const images = Object.values(pack.images);
    return images.length
        ? [pack.skeleton, pack.atlas, ...images]
        : [pack.skeleton];
}

export function gnomeAssetFilePlan(
    equipment: Readonly<GnomeEquipmentSelection>,
) {
    const packIds = resolveGnomePackIds(
        gnomeSpinePackAssets,
        Object.values(equipment).filter((skin) => skin !== null),
    );
    const urls = [
        ...new Set(
            packIds.flatMap((id) => {
                const pack = gnomeSpinePackAssets.packs[id];
                if (!pack) throw new Error(`Missing Gnome pack ${id}`);
                return gnomePackFileUrls(pack);
            }),
        ),
    ];
    return { packIds, urls };
}

export function acquireGnomeAssetFiles(
    equipment: Readonly<GnomeEquipmentSelection>,
    priority: GnomeAssetPriority,
): GnomeAssetLease<void> {
    const leases = gnomeAssetFilePlan(equipment).urls.map((url) =>
        gnomeAssetTransport.acquire(url, priority),
    );
    return {
        ready: Promise.all(leases.map((lease) => lease.ready)).then(
            () => undefined,
        ),
        promote: () => leases.forEach((lease) => lease.promote()),
        release: () => leases.forEach((lease) => lease.release()),
    };
}

export function getGnomeAssetFileState(
    equipment: Readonly<GnomeEquipmentSelection>,
) {
    return gnomeAssetTransport.state(gnomeAssetFilePlan(equipment).urls);
}
